import { Tooltip } from '@chakra-ui/tooltip';
import {
  IActionReference,
  IAuthorActionReference,
  IEditorialActionReference
} from '@publishing-engine/domain/models';
import { Children, cloneElement, ReactNode } from 'react';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import useAuthAccessQuery from 'src/hooks/useAuthAccessQuery';

interface Props {
  actionName: IActionReference['name'];
  children: JSX.Element | JSX.Element[];
  entityType?: string;
  tooltipLabel?: ReactNode;
}

/**
 * This component takes in an action name and checks if the user has access to the action
 * This is not meant for reviews as it only uses `useSubmissionData()`
 */

const ProtectedWorkflowAction = ({
  actionName,
  children,
  entityType = 'journal_phase',
  tooltipLabel = 'You do not have permission to perform this action'
}: Props) => {
  const { workflowResponse, submissionContextId, submissionId } =
    useSubmissionData();
  const currentState = workflowResponse.workflowData.currentState;
  const actions =
    workflowResponse.articleWorkflow.transitionGraph.submission.states[
      currentState
    ]?.actions || [];

  const action = actions.find(action => action.name === actionName) as
    | IEditorialActionReference
    | IAuthorActionReference;
  const entityId =
    action?.roleType === 'author'
      ? `${submissionId}`
      : `${submissionContextId}_${currentState}`;

  const updatedEntityType =
    action?.roleType === 'author' ? 'submission' : entityType;

  const { isAllowed, isLoading } = useAuthAccessQuery({
    relation:
      action?.roleType === 'author' ? action?.roleType : action?.accessLevel,
    entityType: updatedEntityType,
    entityId
  });

  return (
    <>
      {isAllowed
        ? children
        : Children.map(children, (child, index) => (
            <Tooltip
              key={index}
              isDisabled={isLoading}
              shouldWrapChildren
              hasArrow
              label={tooltipLabel}
              fontSize="md"
              placement="top"
              modifiers={[
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                    padding: 8,
                    altAxis: true
                  }
                },
                {
                  name: 'flip',
                  options: { boundary: 'viewport', padding: 8 }
                }
              ]}
            >
              <span style={{ display: 'inline-flex' }}>
                {cloneElement(child, {
                  isDisabled: true,
                  isLoading,
                  onClick: (e: Event) => e.preventDefault()
                })}
              </span>
            </Tooltip>
          ))}
    </>
  );
};

export default ProtectedWorkflowAction;
